import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Spinner } from '@openedx/paragon';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';

import { getUserSurveyAnswers, parseSurveyJson } from './data/api';
import messages from './messages';

const SurveyResults = ({
  courseId,
  courseSurvey,
  intl,
  onReturn,
}) => {
  const [answerRecord, setAnswerRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadAnswers() {
      setLoading(true);
      setLoadError(false);
      try {
        const records = await getUserSurveyAnswers(courseId);
        const matchingRecord = (Array.isArray(records) ? records : [])
          .find(record => String(record.course_survey) === String(courseSurvey.id));
        if (isMounted) {
          setAnswerRecord(matchingRecord || null);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadAnswers();

    return () => {
      isMounted = false;
    };
  }, [courseId, courseSurvey.id]);

  const answers = useMemo(() => {
    const parsedAnswers = parseSurveyJson(answerRecord?.answers, []);
    return Array.isArray(parsedAnswers) ? parsedAnswers : [];
  }, [answerRecord]);

  const formatAnswer = (answer) => {
    if (answer === undefined || answer === null || answer === '') {
      return intl.formatMessage(messages.noAnswerProvided);
    }

    return String(answer);
  };

  if (loading) {
    return (
      <div className="survey-loading">
        <Spinner
          animation="border"
          variant="primary"
          screenReaderText={intl.formatMessage(messages.loadingAnswers)}
        />
      </div>
    );
  }

  if (loadError) {
    return <Alert variant="danger">{intl.formatMessage(messages.loadError)}</Alert>;
  }

  return (
    <div className="survey-results">
      <div className="survey-form-header">
        <h2 className="h3">{courseSurvey.survey_name}</h2>
        {courseSurvey.survey_description && (
          <p className="text-gray-700 mb-0">{courseSurvey.survey_description}</p>
        )}
      </div>

      {answers.length === 0 && (
        <Alert variant="info">
          {intl.formatMessage(messages.noAnswersFound)}
        </Alert>
      )}

      {answers.map(answer => (
        <div key={answer.id} className="survey-answer">
          <div className="survey-question-label">{answer.question}</div>
          <div className="text-gray-700">{formatAnswer(answer.answer)}</div>
        </div>
      ))}

      <Button type="button" variant="outline-primary" onClick={onReturn}>
        {intl.formatMessage(messages.goBack)}
      </Button>
    </div>
  );
};

SurveyResults.propTypes = {
  courseId: PropTypes.string.isRequired,
  courseSurvey: PropTypes.shape({
    id: PropTypes.number.isRequired,
    survey_name: PropTypes.string,
    survey_description: PropTypes.string,
  }).isRequired,
  intl: intlShape.isRequired,
  onReturn: PropTypes.func.isRequired,
};

export default injectIntl(SurveyResults);
